
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "@/contexts/ProjectContext";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, CheckCircle, Clock, Download, FileCheck, FolderOpen, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InformationExchange as InformationExchangeType } from "@/types";

export default function InformationExchange() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const [selectedStage, setSelectedStage] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  if (!currentProject) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-1 container py-6 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">No project selected</h2>
            <p className="text-muted-foreground mb-4">Please select a project first</p>
            <Button onClick={() => navigate("/projects-selector")}>
              Go to Projects
            </Button>
          </div>
        </main>
      </div>
    );
  }
  
  // Get all information exchanges from all stages
  const allInformationExchanges = currentProject.stages.flatMap(stage => {
    if (!stage.informationExchanges) return [];
    return stage.informationExchanges.map(exchange => ({
      ...exchange,
      stageName: stage.name,
      stageId: stage.id
    }));
  });
  
  // Filter exchanges based on selected filters
  const filteredExchanges = allInformationExchanges.filter(exchange => {
    if (selectedStage !== "all" && exchange.stageId !== parseInt(selectedStage)) {
      return false;
    }
    
    if (selectedStatus !== "all" && exchange.status !== selectedStatus) {
      return false;
    }
    
    if (searchTerm && !exchange.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !exchange.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  const getStatusClass = (status: string) => {
    switch (status) {
      case "validated":
        return "bg-green-100 text-green-800";
      case "complete":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "validated":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "complete":
        return <FileCheck className="h-5 w-5 text-blue-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };
  
  // Calculate completion statistics
  const stats = {
    total: allInformationExchanges.length,
    complete: allInformationExchanges.filter(ex => ex.status === "complete").length,
    validated: allInformationExchanges.filter(ex => ex.status === "validated").length,
    pending: allInformationExchanges.filter(ex => ex.status === "pending").length,
  };

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <main className="flex-1 container py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Information Exchange</h1>
            <p className="text-muted-foreground">
              Manage and track required deliverables across all RIBA stages
            </p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total Items</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-gray-400" />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Complete</p>
                <p className="text-2xl font-bold">{stats.complete}</p>
              </div>
              <FileCheck className="h-8 w-8 text-blue-500" />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Validated</p>
                <p className="text-2xl font-bold">{stats.validated}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Pending</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </CardContent>
          </Card>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Filter Information Exchange Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Select value={selectedStage} onValueChange={setSelectedStage}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by RIBA Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="0">Stage 0: Strategic Definition</SelectItem>
                  <SelectItem value="1">Stage 1: Preparation and Briefing</SelectItem>
                  <SelectItem value="2">Stage 2: Concept Design</SelectItem>
                  <SelectItem value="3">Stage 3: Spatial Coordination</SelectItem>
                  <SelectItem value="4">Stage 4: Technical Design</SelectItem>
                  <SelectItem value="5">Stage 5: Manufacturing and Construction</SelectItem>
                  <SelectItem value="6">Stage 6: Handover</SelectItem>
                  <SelectItem value="7">Stage 7: Use</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="validated">Validated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Items List */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle>Information Exchange Items</CardTitle>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" /> Export List
              </Button>
            </div>
            <CardDescription>
              {filteredExchanges.length} items found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredExchanges.length > 0 ? (
                filteredExchanges.map((exchange, index) => (
                  <div 
                    key={exchange.id} 
                    className="border rounded-md p-4 hover:border-blue-300 transition-colors cursor-pointer"
                    onClick={() => navigate(`/stage/${exchange.stageId}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{exchange.name}</h3>
                          {exchange.requiredForSignoff && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded ${getStatusClass(exchange.status)}`}>
                            {exchange.status.charAt(0).toUpperCase() + exchange.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{exchange.description}</p>
                      </div>
                      
                      <div className="flex items-center">
                        {getStatusIcon(exchange.status)}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>Stage {exchange.stageId}: {exchange.stageName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 mr-1" />
                        <span>{exchange.dateUploaded ? new Date(exchange.dateUploaded).toLocaleDateString() : "No upload date"}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-1">No items found</h3>
                  <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
                  <Button variant="outline" onClick={() => {
                    setSearchTerm("");
                    setSelectedStage("all");
                    setSelectedStatus("all");
                  }}>
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
